import { ListParams, queryStringFromListParams } from '@wwf/sdk';
import type { GetListParams, GetManyReferenceParams } from 'react-admin';

export { queryStringFromListParams };

export type ApiRecord = Record<string, unknown>;

export type ListResult = { data: ApiRecord[]; total: number };

export type Page<T> = { content: T[]; totalElements: number };

export function toRecord(item: unknown): ApiRecord {
    return { ...(item as ApiRecord) };
}

type ExtraParamsSource = {
    filter?: Record<string, unknown>;
    meta?: Record<string, unknown>;
};

export function collectExtraParams(
    extraParamKeys: string[] | undefined,
    params: GetListParams | GetManyReferenceParams | ExtraParamsSource
): Record<string, string> {
    const extra: Record<string, string> = {};
    const filter = params.filter as Record<string, unknown> | undefined;
    const meta = params.meta as Record<string, unknown> | undefined;
    for (const key of extraParamKeys ?? []) {
        const value = meta?.[key] ?? filter?.[key];
        if (value != null) extra[key] = String(value);
    }
    return extra;
}

export function appendExtraQueryParams(
    query: string,
    extra: Record<string, string>
): string {
    const suffix = new URLSearchParams(extra).toString();
    if (!suffix) return query;
    return query ? `${query}&${suffix}` : `?${suffix}`;
}

export type ReactAdminListInput = {
    pagination?: { page: number; perPage: number } | null;
    sort?:
        | { field: string; order: 'ASC' | 'DESC' }
        | { field: string; order?: 'ASC' | 'DESC' }[];
    filter?: Record<string, unknown>;
};

/** Maps react-admin list params to SDK list params (0-based page for Spring `page`/`size`). */
export function toListParams(input: ReactAdminListInput): ListParams {
    const sort = input.sort;
    const reactAdminPage = input.pagination?.page;

    return {
        page: reactAdminPage != null ? Math.max(0, reactAdminPage - 1) : null,
        perPage: input.pagination?.perPage ?? null,
        sort: sort == null ? undefined : Array.isArray(sort) ? sort : [sort],
        filter: input.filter,
    };
}

/** Strip keys that are passed as plain query params (not PostgREST filters). */
export function toListParamsWithoutExtra(
    input: ReactAdminListInput,
    extraParamKeys: string[] | undefined
): ListParams {
    const listParams = toListParams(input);
    if (!listParams.filter || !extraParamKeys?.length) {
        return listParams;
    }
    const filter = { ...listParams.filter };
    for (const key of extraParamKeys) {
        delete filter[key];
    }
    return { ...listParams, filter };
}
